import {Typography} from "@mui/material";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {RibbonButton, RibbonGroup, RibbonSelect} from "../Ribbon";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {getHingeLines} from "../../../hardware/hingeProducts";

type HingeSel = Extract<MdSelection, {type: "hinge"}>;

// the RibbonSelect value standing in for "no override — use the main hinge product".
const DEFAULT = "default";

// the contextual tab for a selected hinge on the Locations view. When the opening
// has more than one hinge product it offers a per-location product override; with a
// single hinge product there's nothing to pick, so it just names the hinge(s).
export function HingeSelection(props: {app: ElevationApp; selection: HingeSel[]}) {
    const app = props.app;
    const hinges = app.hardware.hinges;

    // re-render when a hinge's override (or the list) changes so the dropdown tracks it.
    useEventEmitter2(hinges.listChanged);

    // the leaf the Locations view is showing; the dropdown filters its products and
    // reads/writes the override for this leaf. Re-render when the view switches.
    const view = useEventEmitter2(app.viewSettings.activeView);
    const leaf = view === "location-inactive" ? "inactive" : "active";

    const selected = props.selection
        .map(s => hinges.list.find(h => h.id === s.hingeId))
        .filter((h): h is NonNullable<typeof h> => !!h);

    const hardware = app.hardwareGroup?.hardware ?? [];
    const hingeProducts = getHingeLines(hardware, leaf);
    // the group-wide default (both leaves) the per-location override falls back to.
    const defaultProduct = hinges.getDefaultProduct(hardware);
    const hasChoice = hingeProducts.length > 1;

    // the selected hinge letter(s), shown big in the no-op "Hinge" button.
    const letters = selected.map(h => h.id).join(" ");
    const label = selected.length === 1 ? "Hinge" : "Hinges";

    const overrideOf = (h: typeof selected[number]) => leaf === "active" ? h.activeProductOverride : h.inactiveProductOverride;

    // the shared override across the selection: the common value when they agree,
    // else "" (blank) so the dropdown reads as mixed.
    const vals = selected.map(h => overrideOf(h) == null ? DEFAULT : overrideOf(h)!.toString());
    const value = vals.every(v => v === vals[0]) ? (vals[0] ?? DEFAULT) : "";

    // the default may be a product not in this leaf's filtered list, so look it up in
    // the full hardware so the "Default (code)" label always resolves.
    const defaultItem = hardware.find(h => h.product === defaultProduct);
    const options = [
        {value: DEFAULT, label: defaultItem ? `Default (${defaultItem.productCode})` : "Default"},
        ...hingeProducts.map(h => ({value: h.product.toString(), label: h.productCode})),
    ];

    // apply the picked product to every selected hinge on the shown leaf. The default
    // option (or the product that already IS the default) clears the override to null
    // so the hinge follows the main product and drops its canvas label.
    const setProduct = (v: string) => {
        const picked = v === DEFAULT ? null : parseInt(v);
        const normalized = picked != null && picked === defaultProduct ? null : picked;
        for (const h of selected) hinges.setProductOverride(h.id, leaf, normalized);
    };

    return (
        <>
            <RibbonGroup label={label}>
                <RibbonButton
                    icon={<Typography sx={{fontSize: 38, fontWeight: 700, lineHeight: 1.3}}>{letters}</Typography>}
                    label={""}
                    onClick={() => {}}
                />
            </RibbonGroup>

            {hasChoice && (
                <RibbonGroup label="Product">
                    <RibbonSelect value={value} options={options} onChange={setProduct} />
                </RibbonGroup>
            )}
        </>
    );
}
